import { ArrayLikeUtils } from './ArrayLikeUtils';
import { IDataStore } from './IDataStore';
import { PaneStateAttribute } from './PaneStateAttribute';

/**
 * Centralized datastore paths and accessors.
 * C++ Reference: RenderingInputStoreReader, RenderingInputStoreKeys
 *
 * Note: This class becomes unnecessary when W-19387372 lands (see IDataStore.ts).
 */
export class DataStorePaths {
  static readonly VIEWPOINT = 'Viewpoint';
  static readonly PANE_EXTENTS = `${this.VIEWPOINT}.PerPaneExtents`;
  static readonly PANE_KEYS = `${this.PANE_EXTENTS}.PaneKey`;
  static readonly PDMARKS = 'PDMarks';

  static readonly PAGE_INTERVAL_TABLE = 'PageIntervalTable';

  static readonly PAGE_INTERVAL_START = `${this.PAGE_INTERVAL_TABLE}.StartIndex`;
  static readonly PAGE_INTERVAL_END = `${this.PAGE_INTERVAL_TABLE}.EndIndex`;

  static readonly PAGE_INTERVAL_FIELDS = [
    `${this.PAGE_INTERVAL_TABLE}.StartIndex`,
    `${this.PAGE_INTERVAL_TABLE}.EndIndex`,
    `${this.PAGE_INTERVAL_TABLE}.PaneKey`,
    `${this.PAGE_INTERVAL_TABLE}.PaneCol`,
    `${this.PAGE_INTERVAL_TABLE}.PaneRow`,
    `${this.PAGE_INTERVAL_TABLE}.PaneLayer`,
    `${this.PAGE_INTERVAL_TABLE}.PanePage`,
  ] as const;

  static readonly MAP_DOMAIN_LEFT = `${this.PANE_EXTENTS}.MapDomainLeft`;
  static readonly MAP_DOMAIN_RIGHT = `${this.PANE_EXTENTS}.MapDomainRight`;
  static readonly MAP_DOMAIN_BOTTOM = `${this.PANE_EXTENTS}.MapDomainBottom`;
  static readonly MAP_DOMAIN_TOP = `${this.PANE_EXTENTS}.MapDomainTop`;

  // C++ HasPDMarks()
  static hasPDMarks(ds: IDataStore | null): boolean {
    if (ds === null) {
      return false;
    }
    if (this.paneKeys(ds).length === 0) {
      return false;
    }
    if (!ds.hasValue(this.PDMARKS)) {
      return false;
    }
    return true;
  }

  static paneKeys(ds: IDataStore): string[] {
    const value: unknown = ds.getValue(this.PANE_KEYS);
    if (Array.isArray(value) && value.every((v): v is string => typeof v === 'string')) {
      return value;
    }

    const arr = ArrayLikeUtils.toArray<string>(value);
    if (arr.length > 0 && arr.every((v) => typeof v === 'string')) {
      return arr;
    }

    return [];
  }

  static getPaneDescriptorsSet(ds: IDataStore | null): Set<string> {
    if (ds === null) {
      return new Set();
    }
    return new Set(this.paneKeys(ds));
  }

  static markIds(ds: IDataStore, paneId: string): number[] {
    const path = `${this.PDMARKS}.${paneId}.Encodings.MarkId`;
    const value: unknown = ds.getValue(path);
    return ArrayLikeUtils.toArray<number>(value);
  }

  static primitiveType(ds: IDataStore, paneId: string): number {
    const path = `${this.PDMARKS}.${paneId}.MarkLayoutData.Parameters.PrimitiveType`;
    const value: unknown = ds.getValue(path);
    if (typeof value === 'number') {
      return value;
    }
    return 0;
  }

  // C++ GetPaneDescriptorsInBothDataStores()
  static getPaneDescriptorsInBoth(store0: IDataStore, store1: IDataStore): string[] {
    const set0 = this.getPaneDescriptorsSet(store0);
    const set1 = this.getPaneDescriptorsSet(store1);
    const result: string[] = [];
    set0.forEach((pane) => {
      if (set1.has(pane)) {
        result.push(pane);
      }
    });
    return result;
  }

  // C++ GetPaneDescriptorsInStore1Only()
  static getPaneDescriptorsInStore1Only(store0: IDataStore, store1: IDataStore): string[] {
    const set0 = this.getPaneDescriptorsSet(store0);
    const set1 = this.getPaneDescriptorsSet(store1);
    const result: string[] = [];
    set1.forEach((pane) => {
      if (!set0.has(pane)) {
        result.push(pane);
      }
    });
    return result;
  }

  // C++ GetPanesToAnimate() - returns intersection + entering panes
  static getPanesToAnimate(store0: IDataStore | null, store1: IDataStore): string[] {
    if (store0 === null) {
      return Array.from(this.getPaneDescriptorsSet(store1));
    }

    const panesToAnimate = this.getPaneDescriptorsInBoth(store0, store1);
    if (panesToAnimate.length > 0) {
      panesToAnimate.push(...this.getPaneDescriptorsInStore1Only(store0, store1));
    }
    return panesToAnimate;
  }

  /**
   * Map PaneStateAttribute enum to datastore path.
   * Mirrors C++ DataStorePaneState::GetKeyPathForAttribute()
   *
   * C++ Reference: /monolith/modules/platform/tabanim/main/datastorepanestate/DataStorePaneState.cpp:1301-1441
   *
   * @param paneId - Pane descriptor ID (e.g., '[pane0]')
   * @param attribute - The attribute to get the path for
   * @returns Datastore path string, or null if attribute not mapped
   */
  static getKeyPathForAttribute(paneId: string, attribute: PaneStateAttribute): string | null {
    switch (attribute) {
      // Mark position attributes
      case PaneStateAttribute.MarkXPosition:
        return `${this.PDMARKS}.${paneId}.Encodings.x`;
      case PaneStateAttribute.MarkYPosition:
        return `${this.PDMARKS}.${paneId}.Encodings.y`;
      case PaneStateAttribute.MarkXFarPosition:
        return `${this.PDMARKS}.${paneId}.Encodings.x1`;
      case PaneStateAttribute.MarkYFarPosition:
        return `${this.PDMARKS}.${paneId}.Encodings.y1`;
      case PaneStateAttribute.MarkXPrev:
        return `${this.PDMARKS}.${paneId}.Encodings.x_prev`;
      case PaneStateAttribute.MarkYPrev:
        return `${this.PDMARKS}.${paneId}.Encodings.y_prev`;
      case PaneStateAttribute.MarkXNext:
        return `${this.PDMARKS}.${paneId}.Encodings.x_next`;
      case PaneStateAttribute.MarkYNext:
        return `${this.PDMARKS}.${paneId}.Encodings.y_next`;

      // Mark visual attributes
      case PaneStateAttribute.MarkSize:
        return `${this.PDMARKS}.${paneId}.Encodings.size`;
      case PaneStateAttribute.MarkColor:
        return `${this.PDMARKS}.${paneId}.Encodings.color`;
      case PaneStateAttribute.MarkWeight:
        return `${this.PDMARKS}.${paneId}.Encodings.weight`;

      // Mark angle attributes
      case PaneStateAttribute.Theta:
        return `${this.PDMARKS}.${paneId}.Encodings.theta`;
      case PaneStateAttribute.Theta1:
        return `${this.PDMARKS}.${paneId}.Encodings.theta1`;

      // Special case: HeatmapMarkWeight in different bundle
      // C++ mapping: storeKeys.Heatmap().Encoding(paneKey).MarkWeights()
      case PaneStateAttribute.HeatmapMarkWeight:
        return `Heatmap.Encoding.${paneId}.MarkWeights`;

      // Label attributes (not currently animated for marks, but included for completeness)
      case PaneStateAttribute.LabelX:
        return `AnnexedData.LabelStore.${this.PDMARKS}.${paneId}.Encodings.x`;
      case PaneStateAttribute.LabelY:
        return `AnnexedData.LabelStore.${this.PDMARKS}.${paneId}.Encodings.y`;
      case PaneStateAttribute.LabelWidth:
        return `AnnexedData.LabelStore.${this.PDMARKS}.${paneId}.Encodings.width`;
      case PaneStateAttribute.LabelHeight:
        return `AnnexedData.LabelStore.${this.PDMARKS}.${paneId}.Encodings.height`;

      // Pane bounds attributes (Viewpoint.PerPaneExtents arrays)
      // These are indexed by PaneKey, not MarkId
      // Note: Runtime uses snake_case (pane_px_top) not PascalCase (PxTop)
      case PaneStateAttribute.PaneRectBottom:
        return 'Viewpoint.PerPaneExtents.pane_px_bottom';
      case PaneStateAttribute.PaneRectTop:
        return 'Viewpoint.PerPaneExtents.pane_px_top';
      case PaneStateAttribute.PaneRectLeft:
        return 'Viewpoint.PerPaneExtents.pane_px_left';
      case PaneStateAttribute.PaneRectRight:
        return 'Viewpoint.PerPaneExtents.pane_px_right';
      case PaneStateAttribute.PaneRectDomainBottom:
        return 'Viewpoint.PerPaneExtents.DomainBottom';
      case PaneStateAttribute.PaneRectDomainTop:
        return 'Viewpoint.PerPaneExtents.DomainTop';
      case PaneStateAttribute.PaneRectDomainLeft:
        return 'Viewpoint.PerPaneExtents.DomainLeft';
      case PaneStateAttribute.PaneRectDomainRight:
        return 'Viewpoint.PerPaneExtents.DomainRight';
      case PaneStateAttribute.PaneRectOriginX:
        return 'Viewpoint.PerPaneExtents.OriginX';
      case PaneStateAttribute.PaneRectOriginY:
        return 'Viewpoint.PerPaneExtents.OriginY';
      case PaneStateAttribute.XRangeMin:
        return 'Viewpoint.PerPaneExtents.XRangeMin';
      case PaneStateAttribute.XRangeMax:
        return 'Viewpoint.PerPaneExtents.XRangeMax';
      case PaneStateAttribute.YRangeMin:
        return 'Viewpoint.PerPaneExtents.YRangeMin';
      case PaneStateAttribute.YRangeMax:
        return 'Viewpoint.PerPaneExtents.YRangeMax';
      case PaneStateAttribute.MapDomainBottom:
        return 'Viewpoint.PerPaneExtents.MapDomainBottom';
      case PaneStateAttribute.MapDomainTop:
        return 'Viewpoint.PerPaneExtents.MapDomainTop';
      case PaneStateAttribute.MapDomainLeft:
        return 'Viewpoint.PerPaneExtents.MapDomainLeft';
      case PaneStateAttribute.MapDomainRight:
        return 'Viewpoint.PerPaneExtents.MapDomainRight';

      case PaneStateAttribute.UserPaneXCellOffsets:
        return 'AnnexedData.UserPaneInfo.XCellOffsets';
      case PaneStateAttribute.UserPaneYCellOffsets:
        return 'AnnexedData.UserPaneInfo.YCellOffsets';
      case PaneStateAttribute.UserPaneXPixelOffsets:
        return 'AnnexedData.UserPaneInfo.XPixelOffsets';
      case PaneStateAttribute.UserPaneYPixelOffsets:
        return 'AnnexedData.UserPaneInfo.YPixelOffsets';
      case PaneStateAttribute.UserPaneXCellWidths:
        return 'AnnexedData.UserPaneInfo.XCellWidths';
      case PaneStateAttribute.UserPaneYCellHeights:
        return 'AnnexedData.UserPaneInfo.YCellHeights';
      case PaneStateAttribute.UserPaneXPixelWidths:
        return 'AnnexedData.UserPaneInfo.XPixelWidths';
      case PaneStateAttribute.UserPaneYPixelHeights:
        return 'AnnexedData.UserPaneInfo.YPixelHeights';

      // Heatmap non-mark attributes
      case PaneStateAttribute.HeatmapMinValue:
        return `Heatmap.Encoding.${paneId}.MinValue`;
      case PaneStateAttribute.HeatmapMaxValue:
        return `Heatmap.Encoding.${paneId}.MaxValue`;

      // Grid attributes
      // C++ mapping: storeKeys.GridLines().X().LineColor()
      case PaneStateAttribute.GridXLineColor:
        return 'GridLines.X.LineColor';
      // C++ mapping: storeKeys.GridLines().Y().LineColor()
      case PaneStateAttribute.GridYLineColor:
        return 'GridLines.Y.LineColor';
      // C++ mapping: storeKeys.GridLines().ZeroPoint()
      case PaneStateAttribute.GridZeroPoint:
        return 'GridLines.ZeroPoint';
      // C++ mapping: storeKeys.TickPlacement().PerHeaderMajorTickPlacement().Origin()
      case PaneStateAttribute.GridOrigin:
        return 'TickPlacement.PerHeaderMajorTickPlacement.Origin';
      // C++ mapping: storeKeys.TickPlacement().PerHeaderMajorTickPlacement().Spacing()
      case PaneStateAttribute.GridSpacing:
        return 'TickPlacement.PerHeaderMajorTickPlacement.Spacing';

      // Axis attributes
      case PaneStateAttribute.XAxisMin:
        return 'AxisOutput.XAxisTable.Min';
      case PaneStateAttribute.XAxisMax:
        return 'AxisOutput.XAxisTable.Max';
      case PaneStateAttribute.YAxisMin:
        return 'AxisOutput.YAxisTable.Min';
      case PaneStateAttribute.YAxisMax:
        return 'AxisOutput.YAxisTable.Max';
      // C++ mapping: storeKeys.SceneMargin().Styles().BorderStyles().X().CustomDividerPenColor()
      case PaneStateAttribute.XAxisDividerColor:
        return 'SceneMargin.Styles.BorderStyles.X.CustomDividerPenColor';
      // C++ mapping: storeKeys.SceneMargin().Styles().BorderStyles().Y().CustomDividerPenColor()
      case PaneStateAttribute.YAxisDividerColor:
        return 'SceneMargin.Styles.BorderStyles.Y.CustomDividerPenColor';
      // C++ mapping: storeKeys.AxisOutput().Style().TickColor()
      case PaneStateAttribute.AxisTickColor:
        return 'AxisOutput.Style.TickColor';
      // C++ mapping: storeKeys.AxisOutput().XAxisTable().SortDirection()
      case PaneStateAttribute.XSortDirection:
        return 'AxisOutput.XAxisTable.SortDirection';
      // C++ mapping: storeKeys.AxisOutput().YAxisTable().SortDirection()
      case PaneStateAttribute.YSortDirection:
        return 'AxisOutput.YAxisTable.SortDirection';

      default:
        return null;
    }
  }
}
